import { DRAFT_KINDS, DRAFTS, LANG_LABELS, splitSubject, type DraftKind, type LetterLang } from '@jt/core';
import type { ApplicationFull } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ApiError, getCsrf, post } from '../api/client';
import { applyFull, keys, useApplication, useSettings, useToday } from '../api/hooks';
import { useUI } from '../components/overlays';
import { Icon } from '../components/ui';

export type { DraftKind };

export const DRAFT_TITLES = Object.fromEntries(DRAFT_KINDS.map((k) => [k, DRAFTS[k].title])) as Record<
  DraftKind,
  string
>;

export const AI_OFF_HINT =
  'Claude недоступен: не задан ключ Anthropic («Настройки» → Claude). Всё остальное работает вручную.';

/** Доступен ли Claude (ключ на сервере). */
export function useAi(): boolean {
  return !!useToday().data?.ai;
}

/** Открыть окно черновика. */
export function useOpenDraft() {
  const ui = useUI();
  const ai = useAi();
  const qc = useQueryClient();
  return (applicationId: string, kind: DraftKind) => {
    if (!ai) return ui.toast(AI_OFF_HINT);
    const a = qc.getQueryData<ApplicationFull>(keys.app(applicationId));
    ui.openSheet({
      title: `${DRAFTS[kind].title}${a?.company ? ' · ' + a.company : ''}`,
      body: <DraftSheet applicationId={applicationId} kind={kind} />,
    });
  };
}

/** Чтение потока SSE из fetch: события delta / done / error (у «Подготовить отклик» — step / done / error). */
export async function readSse<T = { text?: string; message?: string }>(
  res: Response,
  on: (event: string, data: T) => void,
): Promise<void> {
  const reader = res.body!.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const ev = chunk.match(/^event: (.+)$/m)?.[1] ?? 'message';
      const data = chunk.match(/^data: (.+)$/m)?.[1];
      if (data) on(ev, JSON.parse(data) as T);
    }
  }
}

/** Последний черновик по вакансии и виду письма — в этом браузере, переживает закрытие окна. */
const draftKey = (applicationId: string, kind: DraftKind) => `jt.draft.${applicationId}.${kind}`;
interface Saved {
  text: string;
  lang: LetterLang;
  at: string;
}
function loadDraft(applicationId: string, kind: DraftKind): Saved | null {
  try {
    const raw = localStorage.getItem(draftKey(applicationId, kind));
    return raw ? (JSON.parse(raw) as Saved) : null;
  } catch {
    return null;
  }
}
function storeDraft(applicationId: string, kind: DraftKind, d: Saved | null) {
  try {
    if (d?.text) localStorage.setItem(draftKey(applicationId, kind), JSON.stringify(d));
    else localStorage.removeItem(draftKey(applicationId, kind));
  } catch {
    /* приватный режим — просто не сохраняем */
  }
}
const savedAt = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

function DraftSheet({ applicationId, kind: initialKind }: { applicationId: string; kind: DraftKind }) {
  const ui = useUI();
  const qc = useQueryClient();
  const app = useApplication(applicationId);
  const settings = useSettings();
  const [kind, setKind] = useState<DraftKind>(initialKind);
  const initial = loadDraft(applicationId, initialKind);
  const [lang, setLang] = useState<LetterLang | ''>(initial?.lang ?? '');
  const [extra, setExtra] = useState('');
  const [text, setText] = useState(initial?.text ?? '');
  const [status, setStatus] = useState(initial ? `Сохранённый черновик от ${savedAt(initial.at)}` : '');
  const [running, setRunning] = useState(false);
  const ctl = useRef<AbortController | null>(null);
  const langValue = lang || settings.data?.lettersLang || 'en';

  // правки и готовый текст сохраняются сразу — закрытое окно не теряет план или письмо
  useEffect(() => {
    if (running) return;
    storeDraft(applicationId, kind, text ? { text, lang: langValue, at: new Date().toISOString() } : null);
  }, [applicationId, kind, text, langValue, running]);
  const switchKind = (k: DraftKind) => {
    const d = loadDraft(applicationId, k);
    setKind(k);
    setText(d?.text ?? '');
    if (d) setLang(d.lang);
    setStatus(d ? `Сохранённый черновик от ${savedAt(d.at)}` : '');
  };

  // закрытие окна прерывает поток
  useEffect(() => () => ctl.current?.abort(), []);

  const run = async () => {
    ctl.current = new AbortController();
    setRunning(true);
    setText('');
    setStatus('Claude думает…');
    let got = '';
    try {
      const res = await fetch('/api/ai/draft', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-csrf-token': getCsrf() },
        body: JSON.stringify({ applicationId, kind, lang: langValue, extra }),
        credentials: 'same-origin',
        signal: ctl.current.signal,
      });
      if (!res.ok) {
        const e = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        setStatus(e?.error?.message ?? `Ошибка ${res.status}`);
        return;
      }
      await readSse(res, (ev, data) => {
        if (ev === 'delta' && data.text) {
          got += data.text;
          setText(got);
          setStatus('Пишет…');
        } else if (ev === 'done') setStatus('Готово. Проверьте и при необходимости поправьте.');
        else if (ev === 'error') setStatus(data.message ?? 'Не получилось. Повторите.');
      });
    } catch (e) {
      setStatus((e as Error).name === 'AbortError' ? 'Остановлено.' : 'Нет связи с сервером.');
    } finally {
      setRunning(false);
      ctl.current = null;
    }
  };

  const copy = async () => {
    if (!text) return ui.toast('Пока нечего копировать.');
    try {
      await navigator.clipboard.writeText(text);
      ui.toast('Скопировано');
    } catch {
      ui.toast('Не удалось скопировать — выделите текст вручную.');
    }
  };

  const email = app.data?.contacts.find((c) => c.email)?.email ?? '';
  const mailto = () => {
    const { subject, body } = splitSubject(text);
    return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const log = async () => {
    if (!text) return ui.toast('Сначала напишите черновик.');
    try {
      const full = await post<ApplicationFull>('/ai/draft/log', { applicationId, kind, text });
      applyFull(qc, full);
      ui.toast('Записано в историю');
    } catch (e) {
      ui.toast(e instanceof ApiError ? e.message : 'Не сохранилось');
    }
  };

  return (
    <>
      <section className="sec">
        <div className="grid">
          <label className="f">
            Что написать
            <select value={kind} onChange={(e) => switchKind(e.target.value as DraftKind)}>
              {DRAFT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {DRAFTS[k].title}
                </option>
              ))}
            </select>
          </label>
          <label className="f">
            Язык
            <select value={langValue} onChange={(e) => setLang(e.target.value as LetterLang)}>
              {Object.entries(LANG_LABELS).map(([k, t]) => (
                <option key={k} value={k}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="f full">
            Что учесть (необязательно)
            <input
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="Например: могу выйти с 1 ноября, хочу 2 дня удалёнки"
            />
          </label>
        </div>
        <div className="btnrow" style={{ marginTop: 10 }}>
          <button className="btn primary" onClick={run} disabled={running}>
            <Icon name="sparkles" size={16} /> Написать
          </button>
          {running && (
            <button className="btn" onClick={() => ctl.current?.abort()}>
              Остановить
            </button>
          )}
          <button className="btn ghost" onClick={() => ui.openApp(applicationId)}>
            К карточке
          </button>
        </div>
      </section>
      <section className="sec">
        <label className="f">
          Текст (можно править)
          <textarea
            className="out"
            rows={12}
            placeholder="Здесь появится черновик"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <p className="muted sm" role="status">
          {status}
        </p>
        <div className="btnrow">
          <button className="btn sm" onClick={copy}>
            Скопировать
          </button>
          <a
            className="btn sm"
            href={text ? mailto() : undefined}
            aria-disabled={!text}
            onClick={(e) => !text && e.preventDefault()}
          >
            Открыть в почте
          </a>
          <button className="btn sm" onClick={log}>
            Записать как отправленное
          </button>
        </div>
      </section>
    </>
  );
}
